import React from 'react';
import { styles } from './LegacyContactBlock.css';
import CrazyFont from '@jbhi-fi/lanyard-ui/font/CrazyFont';
import { Icon, IconName } from '@jbhi-fi/lanyard-ui/components/Icon';
import { IconProps } from '@jbhi-fi/lanyard-ui/components/Icon/CustomIcon';
import { TrackingProvider, useTrackingContext } from '../../Tracking';
import { useNavigate } from '../../PlatformFunctionality';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import { useIdPrefix } from '../../IdPrefix';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { MarkdownBlock } from '../../MarkdownBlock';

export type LegacyContactBlockProps = {
  variant: 'default' | 'narrow';
  heading?: string;
  subText?: string;
  idComponent?: string;
  chatText: string;
  chatUrl?: string;
  chatOnClick?: () => void;
  shouldHideCallUs?: boolean;
  callText?: string;
  callUrl?: string;
  callOnClick?: () => void;
  salesLineHours?: string;
  disclaimerText?: string;
};

type ContactButtonProps = {
  icon: IconName;
  text: string;
  link?: string;
  onClickHandler?: () => void;
  iconProps?: IconProps;
  testId?: string;
};

const ContactButton = ({ icon, iconProps, text, link, onClickHandler, testId }: ContactButtonProps) => {
  const { tracker } = useTrackingContext();
  const navigate = useNavigate();

  const handleOnClick = () => {
    tracker.trackClick({ label: text, extraData: { moduleName: 'Contact block' } });
    if (link) {
      navigate(link);
    } else if (onClickHandler) {
      onClickHandler();
    }
  };

  return (
    <Button
      type="linkWithIcon"
      className={styles.actionButton}
      iconPosition="left"
      onClick={handleOnClick}
      icon={
        <span className={styles.icon}>
          <Icon {...iconProps} name={icon} />
        </span>
      }
      data-testid={testId}
    >
      {text}
    </Button>
  );
};

export const LegacyContactBlock = ({
  variant = 'default',
  heading,
  subText,
  chatText,
  chatUrl,
  chatOnClick,
  shouldHideCallUs,
  callText,
  callUrl,
  callOnClick,
  salesLineHours,
  disclaimerText,
}: LegacyContactBlockProps) => {
  const suffixId = '';
  const chatId = useIdPrefix(`${suffixId}chat`);
  const callId = useIdPrefix(`${suffixId}call`);
  const isDesktop = useBreakpoint('lg', 'up');
  const isDefaultVariant = variant === 'default' && isDesktop;
  const effectiveVariant = !isDesktop ? 'narrow' : variant;

  return (
    <TrackingProvider shouldInheritFromParentContexts group={heading}>
      <div className={styles.container({ variant: effectiveVariant })} id={useIdPrefix(`${suffixId}contact-block`)}>
        <div className={styles.upperContent({ variant: effectiveVariant })}>
          {subText ? (
            <CrazyFont
              variant="withShadowAndOutline"
              as={isDefaultVariant ? 'h1' : 'h3'}
              className={styles.subText({ variant: effectiveVariant })}
            >
              {subText}
            </CrazyFont>
          ) : null}
          {heading ? (
            <CrazyFont
              variant="withShadowAndOutline"
              as={isDefaultVariant ? 'h1' : 'h3'}
              className={styles.heading({ variant: effectiveVariant })}
            >
              {heading}!
            </CrazyFont>
          ) : null}

          <div className={styles.actionWrapper({ variant: effectiveVariant })}>
            <ContactButton icon="Chat" text={chatText} link={chatUrl} onClickHandler={chatOnClick} testId={chatId} />
            {!shouldHideCallUs && callText ? (
              <ContactButton
                icon="Phone"
                iconProps={{ style: { marginRight: '-0.5em' } }}
                text={callText}
                link={callUrl}
                onClickHandler={callOnClick}
                testId={callId}
              />
            ) : null}
            <div className={styles.salesLineHours}>{salesLineHours}</div>
          </div>
        </div>
        {disclaimerText ? (
          <div className={styles.exclusions({ variant: effectiveVariant })}>
            <MarkdownBlock>{disclaimerText}</MarkdownBlock>
          </div>
        ) : null}
      </div>
    </TrackingProvider>
  );
};
